import { create } from 'zustand';
import { readGuideCollapsed, writeGuideCollapsed } from '../components/guide/guide-state.ts';
import { ingestResources } from '../economy/live-store.ts';
import type { ActiveWork } from '../api/construction-overview.ts';
import type { OnboardingStateDto, GalaxyMapResponse, StarSystemClient, MeResponse, FleetClient, CivilizationResources, CivilizationPublic, DiplomacyEntry, BattleReport, ConflictReport, BattleEvent, BattleReplayData } from '@galaxis/shared';

export interface LiveBattleState {
  battleId: string;
  replayData: BattleReplayData;
  attackerCivId: string;
  defenderCivId: string;
  events: BattleEvent[];
  isComplete: boolean;
  winner: 'attacker' | 'defender' | 'draw' | null;
}

interface GameState {
  me: MeResponse | null;
  galaxyMap: GalaxyMapResponse | null;
  fleets: FleetClient[];
  resources: CivilizationResources | null;
  activeWork: ActiveWork[];
  setActiveWork: (items: ActiveWork[]) => void;
  knownCivilizations: CivilizationPublic[];
  diplomacy: DiplomacyEntry[];
  battles: BattleReport[];
  selectedBattle: BattleReport | null;
  conflicts: ConflictReport[];
  liveBattle: LiveBattleState | null;
  selectedSystemId: string | null;
  selectedSystem: StarSystemClient | null;
  activeTab: 'galaxy' | 'empire' | 'research' | 'construction' | 'fleets' | 'diplomacy' | 'trade' | 'community' | 'account';
  /** Bumped on every diplomacy.updated push: views refetch. */
  diplomacyVersion: number;
  bumpDiplomacy: () => void;
  researchVersion: number;
  bumpResearch: () => void;
  selectedFleetId: string | null;
  /** One-shot request for the galaxy map to center on a system (consumed by GalaxyCanvas). */
  mapFocusSystemId: string | null;
  /** M10.4: first-steps guide panel */
  guideOpen: boolean;
  /** The guide is folded to its slim bar (remembered per session / browser, see guide-state) */
  guideCollapsed: boolean;
  /** Server-tracked first-hour state (hooks/useOnboardingSync); drives the guide and the contextual hints */
  onboarding: OnboardingStateDto | null;
  setOnboarding: (s: OnboardingStateDto | null) => void;
  /** M10.6: in-game feedback / bug report dialog */
  feedbackOpen: boolean;
  /** Planned route of a fleet (known systems only, current position first), drawn on the map. */
  plannedRoute: { fleetId: string; path: string[] } | null;
  toasts: Array<{ id: number; text: string; tone: 'info' | 'error' }>;
  isLoading: boolean;
  error: string | null;

  setMe: (me: MeResponse | null) => void;
  setGalaxyMap: (map: GalaxyMapResponse) => void;
  setFleets: (fleets: FleetClient[]) => void;
  updateFleet: (fleet: FleetClient) => void;
  /** Takes a server snapshot (one older than the snapshot held is ignored); `rttMs` refines the clock offset. */
  setResources: (resources: CivilizationResources | null, receivedAt?: number, rttMs?: number) => void;
  setKnownCivilizations: (civs: CivilizationPublic[]) => void;
  setDiplomacy: (entries: DiplomacyEntry[]) => void;
  updateDiplomacyEntry: (withCivId: string, status: DiplomacyEntry['status']) => void;
  setBattles: (battles: BattleReport[]) => void;
  addBattle: (battle: BattleReport) => void;
  selectBattle: (battle: BattleReport | null) => void;
  setConflicts: (conflicts: ConflictReport[]) => void;
  addConflict: (conflict: ConflictReport) => void;
  updateConflict: (conflict: ConflictReport) => void;
  startLiveBattle: (battleId: string, replayData: BattleReplayData, attackerCivId: string, defenderCivId: string) => void;
  addLiveBattleTick: (battleId: string, events: BattleEvent[]) => void;
  completeLiveBattle: (battleId: string, winner: 'attacker' | 'defender' | 'draw') => void;
  clearLiveBattle: () => void;
  updateSystemDiscovery: (systemId: string, status: 'unknown' | 'detected' | 'surveyed' | 'colonized') => void;
  addDetectedSystem: (systemId: string) => void;
  selectSystem: (id: string | null) => void;
  setActiveTab: (tab: GameState['activeTab']) => void;
  selectFleet: (id: string | null) => void;
  showSystemOnMap: (systemId: string) => void;
  clearMapFocus: () => void;
  setGuideOpen: (open: boolean) => void;
  /** `remember`: the player chose it (persisted); automatic folding (e.g. a panel was opened) stays in memory only */
  setGuideCollapsed: (collapsed: boolean, remember?: boolean) => void;
  setFeedbackOpen: (open: boolean) => void;
  setPlannedRoute: (route: { fleetId: string; path: string[] } | null) => void;
  pushToast: (text: string, tone?: 'info' | 'error') => void;
  /** Last discovery the map should highlight (Stellaris fog: a ship just revealed a system). */
  discoveryFlash: { systemId: string; at: number } | null;
  flashDiscovery: (systemId: string) => void;
  dismissToast: (id: number) => void;
  setLoading: (v: boolean) => void;
  setError: (e: string | null) => void;
}

export const useGameStore = create<GameState>((set, get) => ({
  me: null,
  galaxyMap: null,
  fleets: [],
  resources: null,
  activeWork: [],
  setActiveWork: (activeWork) => set({ activeWork }),
  knownCivilizations: [],
  diplomacy: [],
  battles: [],
  selectedBattle: null,
  conflicts: [],
  liveBattle: null,
  selectedSystemId: null,
  selectedSystem: null,
  activeTab: 'galaxy',
  diplomacyVersion: 0,
  researchVersion: 0,
  bumpResearch: () => set(st => ({ researchVersion: st.researchVersion + 1 })),
  bumpDiplomacy: () => set(st => ({ diplomacyVersion: st.diplomacyVersion + 1 })),
  selectedFleetId: null,
  mapFocusSystemId: null,
  guideOpen: false,
  guideCollapsed: readGuideCollapsed(),
  onboarding: null,
  setOnboarding: (onboarding) => set({ onboarding }),
  feedbackOpen: false,
  plannedRoute: null,
  toasts: [],
  isLoading: false,
  error: null,

  setMe: (me) => set({ me }),

  setGalaxyMap: (galaxyMap) => {
    const { selectedSystemId } = get();
    const selectedSystem = selectedSystemId ? (galaxyMap.systems.find(s => s.id === selectedSystemId) ?? null) : null;
    set({ galaxyMap, selectedSystem });
  },

  // Fleet UX: unchanged fleets keep their object identity, so memoized fleet cards and map markers
  // only re-render for the fleets that really changed (a refetch replaces every object otherwise)
  setFleets: (fleets) => {
    const prev = new Map(get().fleets.map(f => [f.id, f]));
    const next = fleets.map(f => {
      const old = prev.get(f.id);
      return old && JSON.stringify(old) === JSON.stringify(f) ? old : f;
    });
    const same = next.length === get().fleets.length && next.every((f, i) => f === get().fleets[i]);
    if (!same) set({ fleets: next });
  },

  setResources: (resources, receivedAt, rttMs) => { if (ingestResources(resources, receivedAt, rttMs)) set({ resources }); },

  setKnownCivilizations: (knownCivilizations) => set({ knownCivilizations }),

  setDiplomacy: (diplomacy) => set({ diplomacy }),

  setBattles: (battles) => set({ battles }),

  addBattle: (battle) => set(state => ({
    battles: [battle, ...state.battles.filter(b => b.id !== battle.id)],
    selectedBattle: battle,
  })),

  selectBattle: (selectedBattle) => set({ selectedBattle }),

  setConflicts: (conflicts) => set({ conflicts }),

  addConflict: (conflict) => set(state => ({
    conflicts: [conflict, ...state.conflicts.filter(c => c.id !== conflict.id)],
  })),

  updateConflict: (conflict) => set(state => ({
    conflicts: state.conflicts.map(c => c.id === conflict.id ? conflict : c),
  })),

  startLiveBattle: (battleId, replayData, attackerCivId, defenderCivId) => set({
    liveBattle: { battleId, replayData, attackerCivId, defenderCivId, events: [], isComplete: false, winner: null },
  }),

  addLiveBattleTick: (battleId, events) => set(state => {
    if (!state.liveBattle || state.liveBattle.battleId !== battleId) return state;
    return { liveBattle: { ...state.liveBattle, events: [...state.liveBattle.events, ...events] } };
  }),

  completeLiveBattle: (battleId, winner) => set(state => {
    if (!state.liveBattle || state.liveBattle.battleId !== battleId) return state;
    return { liveBattle: { ...state.liveBattle, isComplete: true, winner } };
  }),

  clearLiveBattle: () => set({ liveBattle: null }),

  updateDiplomacyEntry: (withCivId, status) => {
    const { diplomacy: current, knownCivilizations } = get();
    const existing = current.find(e => e.civId === withCivId);
    if (existing) {
      set({ diplomacy: current.map(e => e.civId === withCivId ? { ...e, status } : e) });
    } else {
      const civ = knownCivilizations.find(c => c.id === withCivId);
      if (civ) {
        set({ diplomacy: [...current, { civId: withCivId, civName: civ.name, civColor: civ.color, status }] });
      }
    }
  },

  updateFleet: (fleet) => {
    const { fleets } = get();
    const idx = fleets.findIndex(f => f.id === fleet.id);
    if (idx >= 0) {
      const next = [...fleets];
      next[idx] = fleet;
      set({ fleets: next });
    } else {
      set({ fleets: [...fleets, fleet] });
    }
  },

  updateSystemDiscovery: (systemId, status) => {
    const { galaxyMap, selectedSystem } = get();
    if (!galaxyMap) return;
    const updated = galaxyMap.systems.map(s =>
      s.id === systemId ? { ...s, discoveryStatus: status as StarSystemClient['discoveryStatus'] } : s,
    );
    const nextMap = { ...galaxyMap, systems: updated };
    set({ galaxyMap: nextMap });
    // Also update selected system if it's this one
    if (selectedSystem?.id === systemId) {
      set({ selectedSystem: updated.find(s => s.id === systemId) ?? null });
    }
  },

  addDetectedSystem: (systemId) => {
    // After survey.completed, new neighbors become detected — re-fetch or add stub
    // The stub will be filled on next galaxy/map refresh
    const { galaxyMap } = get();
    if (!galaxyMap) return;
    const alreadyKnown = galaxyMap.systems.some(s => s.id === systemId);
    if (!alreadyKnown) {
      // We don't have coordinates yet — refresh triggered by galaxy.discovery.updated handler
    }
  },

  selectSystem: (id) => {
    const { galaxyMap } = get();
    const system = id ? (galaxyMap?.systems.find(s => s.id === id) ?? null) : null;
    set({ selectedSystemId: id, selectedSystem: system });
  },

  setActiveTab: (activeTab) => set({ activeTab }),
  selectFleet: (selectedFleetId) => set({ selectedFleetId }),
  showSystemOnMap: (systemId) => {
    const { galaxyMap } = get();
    const system = galaxyMap?.systems.find(s => s.id === systemId) ?? null;
    set({ activeTab: 'galaxy', selectedSystemId: systemId, selectedSystem: system, mapFocusSystemId: systemId });
  },
  clearMapFocus: () => set({ mapFocusSystemId: null }),
  setGuideOpen: (guideOpen) => set({ guideOpen }),
  setGuideCollapsed: (guideCollapsed, remember = false) => { if (remember) writeGuideCollapsed(guideCollapsed); set({ guideCollapsed }); },
  setFeedbackOpen: (feedbackOpen) => set({ feedbackOpen }),
  setPlannedRoute: (plannedRoute) => set({ plannedRoute }),
  discoveryFlash: null,
  flashDiscovery: (systemId) => set({ discoveryFlash: { systemId, at: Date.now() } }),
  pushToast: (text, tone = 'info') => {
    if (get().toasts.some(t => t.text === text)) return; // the same notice twice (e.g. two sockets) is shown once
    const id = Date.now() + Math.random();
    set(state => ({ toasts: [...state.toasts.slice(-3), { id, text, tone }] }));
    setTimeout(() => get().dismissToast(id), 6000);
  },
  dismissToast: (id) => set(state => ({ toasts: state.toasts.filter(t => t.id !== id) })),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
}));

// Dev / E2E builds only: lets the browser tests put the client into states that need a long game to reach
// (a running live battle, for the update banner). Never present in the production bundle.
if (import.meta.env.DEV) (window as unknown as { __galaxisStore?: typeof useGameStore }).__galaxisStore = useGameStore;
